import {
  selectLightweightElement,
  selectLightweightElementsSlice,
} from "@/store/lightweight-elements-selectors";
import { LightweightElementsState } from "@/store/lightweight-elements-slice";
import { curryAppSelector } from "@/store/reselect";
import { RootState } from "@/store/store";
import { useAppSelector } from "@/store/store-hooks";
import { IElementBase } from "@faro-lotv/ielement-types";
import { createSelector } from "@reduxjs/toolkit";
import { isEqual } from "es-toolkit";
import { useMemo } from "react";
import {
  ElementIconType,
  iconForElement,
  LocationTypes,
} from "./element-icon-type";

type NodeElement = Pick<IElementBase, "id" | "type" | "typeHint">;

/**
 * @returns the icon associated with an Element if available or a simple folder icon
 * @param nodeElement the node attached project element if available
 * @param directParent the type/hints of the node parent if available
 * @param locationType location type for room section
 */
export function useElementIcon(
  nodeElement?: NodeElement,
  directParent?: Pick<IElementBase, "type" | "typeHint">,
  locationType?: LocationTypes,
): ElementIconType {
  const childrenTypeHints = useAppSelector(
    selectChildrenTypeHints(nodeElement),
    isEqual,
  );

  // Specific icon to display for the current node
  return useMemo(() => {
    if (nodeElement) {
      return iconForElement(
        nodeElement.type,
        nodeElement.typeHint,
        childrenTypeHints,
        directParent?.type,
        directParent?.typeHint,
        locationType,
      );
    }
    // Nodes that do not map to an element are folders
    return ElementIconType.FolderIcon;
  }, [
    childrenTypeHints,
    directParent?.type,
    directParent?.typeHint,
    locationType,
    nodeElement,
  ]);
}

/**
 * This memoized selector computes the type hints of the children of a given element.
 * It looks into the lightweight elements slice, and validates that a child really exists
 * in the slice, before returning its type hint.
 *
 * @returns The type hints of the children of the given element
 * @param state The state of the app store
 * @param element The given element
 */
const selectChildrenTypeHints = curryAppSelector(
  createSelector(
    [
      selectLightweightElementsSlice,
      (state: RootState, element: NodeElement | undefined) => element,
    ],
    computeChildrenTypeHints,
  ),
);

function computeChildrenTypeHints(
  lightweightElements: LightweightElementsState,
  element: NodeElement | undefined,
): Array<IElementBase["typeHint"]> {
  if (!element) return [];

  const lwElement = selectLightweightElement(element.id)({
    lightweightElements,
  });
  if (!lwElement || !lwElement.childrenIds) return [];

  const typeHints: Array<IElementBase["typeHint"]> = [];
  for (const childId of lwElement.childrenIds) {
    const child = lightweightElements.lightweightElements[childId];
    if (child) {
      typeHints.push(child.typeHint);
    }
  }
  return typeHints;
}
